import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LifeBuoy,
  BookOpen,
  Building2,
  MapPin,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Loader2,
  Target,
  TrendingUp,
  Clock,
  Languages
} from 'lucide-react';
import {
  getAusbildungsberufe,
  registerRescueCase,
  getMyRescueCase,
  getRescueMatches,
  getRescueStats
} from '../services/api';
import { toast } from 'sonner';

const AusbildungRescue = () => {
  const [berufe, setBerufe] = useState([]);
  const [rescueCase, setRescueCase] = useState(null);
  const [matches, setMatches] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [formData, setFormData] = useState({
    ausbildungsberuf: '',
    current_module: 0,
    abbruch_gruende: {
      sprachprobleme: 0,
      fachliche_probleme: 0,
      betriebliche_probleme: 0,
      persoenliche_probleme: 0,
      finanzielle_probleme: 0
    },
    needs_language_support: false,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [berufeRes, caseRes, statsRes] = await Promise.all([
        getAusbildungsberufe(),
        getMyRescueCase().catch(() => ({ data: null })),
        getRescueStats()
      ]);
      setBerufe(berufeRes.data);
      setRescueCase(caseRes.data);
      setStats(statsRes.data);

      if (caseRes.data) {
        try {
          const matchRes = await getRescueMatches();
          setMatches(matchRes.data);
        } catch {}
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!formData.ausbildungsberuf) {
      toast.error('Bitte wählen Sie einen Ausbildungsberuf');
      return;
    }
    setRegistering(true);
    try {
      const res = await registerRescueCase(formData);
      setRescueCase(res.data);
      toast.success('Rescue-Fall registriert');
      setShowRegister(false);
      const matchRes = await getRescueMatches();
      setMatches(matchRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Registrierung');
    } finally {
      setRegistering(false);
    }
  };

  const getRisikoColor = (kategorie) => {
    const colors = {
      niedrig: 'bg-green-100 text-green-700 border-green-200',
      mittel: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      hoch: 'bg-orange-100 text-orange-700 border-orange-200',
      kritisch: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[kategorie] || colors.niedrig;
  };

  const getRisikoLabel = (kategorie) => {
    const labels = { niedrig: 'Niedrig', mittel: 'Mittel', hoch: 'Hoch', kritisch: 'Kritisch' };
    return labels[kategorie] || 'Unbekannt';
  };

  const selectedBeruf = berufe.find(b => b.key === formData.ausbildungsberuf);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="rescue-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <LifeBuoy className="w-8 h-8 text-primary" />
            Ausbildungs-Rescue
          </h1>
          <p className="text-muted-foreground mt-1">
            Hilfe bei Ausbildungsabbruch - Finden Sie einen neuen Betrieb
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-2xl font-bold text-primary">{stats.active_cases}</p>
                <p className="text-sm text-muted-foreground">Aktive Fälle</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.rescue_partners}</p>
                <p className="text-sm text-muted-foreground">Rescue-Partner</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.berufe_count}</p>
                <p className="text-sm text-muted-foreground">Berufe</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.total_cases}</p>
                <p className="text-sm text-muted-foreground">Gesamt Fälle</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Existing Case or Register */}
        {rescueCase ? (
          <div className="space-y-6">
            {/* Case Overview */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Ihr Rescue-Fall
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Ausbildungsberuf</p>
                    <p className="font-semibold text-lg">
                      {rescueCase.restlaufzeit?.beruf || rescueCase.ausbildungsberuf}
                    </p>
                    <Badge variant="outline" className="mt-1">
                      {rescueCase.restlaufzeit?.kammer}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fortschritt</p>
                    <div className="flex items-center gap-3 mt-1">
                      <Progress
                        value={rescueCase.restlaufzeit?.progress_percent || 0}
                        className="flex-1 h-3"
                      />
                      <span className="font-bold text-primary">
                        {rescueCase.restlaufzeit?.progress_percent || 0}%
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {rescueCase.restlaufzeit?.completed_modules}/{rescueCase.restlaufzeit?.total_modules} Module abgeschlossen
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <Clock className="w-5 h-5 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Restlaufzeit</p>
                    <p className="font-semibold">{rescueCase.restlaufzeit?.remaining_months} Monate</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <BookOpen className="w-5 h-5 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Nächstes Modul</p>
                    <p className="font-semibold text-sm">{rescueCase.restlaufzeit?.next_module || 'Abgeschlossen'}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${getRisikoColor(rescueCase.abbruch_risiko?.kategorie)}`}>
                    <AlertTriangle className="w-5 h-5 mb-2" />
                    <p className="text-sm">Abbruch-Risiko</p>
                    <p className="font-semibold">{getRisikoLabel(rescueCase.abbruch_risiko?.kategorie)}</p>
                  </div>
                </div>

                {/* Empfehlungen */}
                {rescueCase.abbruch_risiko?.empfehlungen?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Empfohlene Maßnahmen</h4>
                    <div className="flex flex-wrap gap-2">
                      {rescueCase.abbruch_risiko.empfehlungen.map((emp, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {emp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Matches */}
            {matches && (
              <div>
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Passende Rescue-Betriebe ({matches.matches?.length || 0})
                </h2>
                {matches.matches?.length > 0 ? (
                  <div className="grid gap-4">
                    {matches.matches.map((match, idx) => (
                      <Card key={idx} className="hover:shadow-md transition-all">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{match.company_name}</h3>
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-4 h-4" />
                                {match.city || 'Standort nicht angegeben'}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {match.match_reasons?.map((reason, ridx) => (
                                  <Badge key={ridx} variant="secondary" className="text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {reason}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="text-center ml-4">
                              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${
                                match.match_score >= 70 ? 'bg-green-100' :
                                match.match_score >= 50 ? 'bg-yellow-100' : 'bg-gray-100'
                              }`}>
                                <span className={`text-lg font-bold ${
                                  match.match_score >= 70 ? 'text-green-700' :
                                  match.match_score >= 50 ? 'text-yellow-700' : 'text-gray-700'
                                }`}>{match.match_score}%</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        Noch keine Rescue-Partner gefunden. Betriebe können sich als Rescue-Partner registrieren.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Registration Form */
          <div>
            {!showRegister ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <LifeBuoy className="w-16 h-16 mx-auto text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Ausbildungs-Rescue</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-6">
                    Haben Sie Probleme in Ihrer Ausbildung oder suchen einen neuen Betrieb?
                    Registrieren Sie sich und finden Sie passende Rescue-Betriebe.
                  </p>
                  <Button
                    onClick={() => setShowRegister(true)}
                    className="rounded-full"
                    data-testid="start-rescue-btn"
                  >
                    Rescue-Fall registrieren
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Rescue-Fall registrieren</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Beruf Selection */}
                  <div className="space-y-2">
                    <Label>Ausbildungsberuf *</Label>
                    <Select
                      value={formData.ausbildungsberuf}
                      onValueChange={(v) => setFormData({ ...formData, ausbildungsberuf: v, current_module: 0 })}
                    >
                      <SelectTrigger data-testid="rescue-beruf-select">
                        <SelectValue placeholder="Beruf wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {berufe.map(b => (
                          <SelectItem key={b.key} value={b.key}>
                            {b.name} ({b.kammer})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Module Selection */}
                  {selectedBeruf && (
                    <div className="space-y-2">
                      <Label>Aktuelles Modul ({formData.current_module}/{selectedBeruf.module_count})</Label>
                      <Slider
                        value={[formData.current_module]}
                        onValueChange={(v) => setFormData({ ...formData, current_module: v[0] })}
                        min={0}
                        max={selectedBeruf.module_count}
                        step={1}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                        {selectedBeruf.module.map((mod, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border text-sm ${
                              idx < formData.current_module
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : idx === formData.current_module
                                ? 'bg-primary/5 border-primary text-primary font-medium'
                                : 'bg-muted border-border text-muted-foreground'
                            }`}
                          >
                            {idx < formData.current_module && <CheckCircle className="w-3 h-3 inline mr-1" />}
                            {mod.name} ({mod.dauer_wochen} Wo.)
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk Factors */}
                  <div className="space-y-4">
                    <Label>Problemfaktoren (0-100)</Label>
                    {[
                      { key: 'sprachprobleme', label: 'Sprachprobleme', icon: Languages },
                      { key: 'fachliche_probleme', label: 'Fachliche Probleme', icon: BookOpen },
                      { key: 'betriebliche_probleme', label: 'Betriebliche Probleme', icon: Building2 },
                      { key: 'persoenliche_probleme', label: 'Persönliche Probleme', icon: AlertTriangle },
                      { key: 'finanzielle_probleme', label: 'Finanzielle Probleme', icon: TrendingUp }
                    ].map(factor => (
                      <div key={factor.key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm flex items-center gap-2">
                            <factor.icon className="w-4 h-4 text-muted-foreground" />
                            {factor.label}
                          </span>
                          <span className="text-sm font-medium">{formData.abbruch_gruende[factor.key]}%</span>
                        </div>
                        <Slider
                          value={[formData.abbruch_gruende[factor.key]]}
                          onValueChange={(v) => setFormData({
                            ...formData,
                            abbruch_gruende: { ...formData.abbruch_gruende, [factor.key]: v[0] }
                          })}
                          min={0} max={100} step={5}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Language Support */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <h4 className="font-medium">Sprachförderung benötigt</h4>
                      <p className="text-sm text-muted-foreground">Benötigen Sie Sprachunterstützung?</p>
                    </div>
                    <Switch
                      checked={formData.needs_language_support}
                      onCheckedChange={(v) => setFormData({ ...formData, needs_language_support: v })}
                    />
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label>Anmerkungen</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Beschreiben Sie Ihre Situation..."
                      rows={3}
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowRegister(false)}
                      className="rounded-full"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleRegister}
                      disabled={registering || !formData.ausbildungsberuf}
                      className="rounded-full flex-1"
                      data-testid="submit-rescue-btn"
                    >
                      {registering && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Rescue-Fall registrieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AusbildungRescue;
